import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSalePriceInput } from "@/lib/useSalePriceInput";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

type SalePriceInputProps = {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  id?: string;
  label?: string;
  subLabel?: string;
  /** Shown on the right side of the label row. */
  labelTrailing?: ReactNode;
  /** Hide the label row entirely (column headers provide context). */
  hideLabel?: boolean;
  /**
   * Show Lac/Crore short label inside the input on the right while typing.
   * Takes precedence over the label-row short hint when true.
   */
  shortLabelInside?: boolean;
  placeholder?: string;
  error?: string | null;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  labelClassName?: string;
  showShortLabelHint?: boolean;
};

/** Compact sale price input — use in modals, forms, or inline rows. */
export function SalePriceInput({
  value,
  onChange,
  id = "sale-price",
  label = "Amount (PKR)",
  subLabel,
  labelTrailing,
  hideLabel = false,
  shortLabelInside = false,
  placeholder = "0",
  error,
  disabled,
  className,
  inputClassName,
  labelClassName,
  showShortLabelHint = false,
}: SalePriceInputProps) {
  const { inputRef, display, shortLabel, handleChange } = useSalePriceInput(value, onChange);
  const labelHint = !shortLabelInside && showShortLabelHint ? shortLabel : null;
  const trailing = labelTrailing ?? labelHint;
  const insideHint = shortLabelInside && shortLabel ? shortLabel : null;

  return (
    <div className={cn("space-y-2", hideLabel && "space-y-0", className)}>
      {!hideLabel ? (
        <div className="flex items-center justify-between gap-x-2 min-h-[1rem]">
          <div className="min-w-0">
            <Label htmlFor={id} className={cn("text-xs text-muted-foreground", labelClassName)}>
              {label}
            </Label>
            {subLabel ? <p className="text-[11px] text-muted-foreground mt-0.5">{subLabel}</p> : null}
          </div>
          {trailing ? (
            <span className="text-[11px] font-medium text-primary shrink-0 leading-none tabular-nums">
              {trailing}
            </span>
          ) : null}
        </div>
      ) : null}
      <div className="relative min-w-0">
        <Input
          ref={inputRef}
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          className={cn(
            "h-10 tabular-nums",
            insideHint ? "pr-[4.75rem]" : null,
            inputClassName,
          )}
          value={display}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={error ? "true" : "false"}
        />
        {insideHint ? (
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] font-medium text-primary tabular-nums">
            {insideHint}
          </span>
        ) : null}
      </div>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

type SalePriceFieldProps = {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  id?: string;
  disabled?: boolean;
};

/** Panel layout for Add/Edit product purchase section. */
export function SalePriceField({ value, onChange, id = "sale-price", disabled }: SalePriceFieldProps) {
  return (
    <div className="rounded-xl border border-border/50 bg-card/50 p-4 space-y-4 h-full flex flex-col">
      <div>
        <p className="text-sm font-semibold text-foreground">Sale price</p>
        <p className="text-xs text-muted-foreground mt-0.5">Asking price for this vehicle (PKR).</p>
      </div>
      <SalePriceInput
        id={id}
        value={value}
        onChange={onChange}
        disabled={disabled}
        inputClassName="glass-input"
        showShortLabelHint
        className="flex-1"
      />
    </div>
  );
}
